import { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import WebScrollView from '../components/WebScrollView';
import { ArrowLeft, Calendar, Check } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { getAccessibleHomeIds } from '../lib/homeMembership';
import { useActiveHome } from '../contexts/ActiveHomeContext';

interface Visit {
  id: string;
  scheduled_at: string | null;
  status: 'requested' | 'proposed' | 'scheduled' | 'completed' | 'cancelled';
  home_id: string;
  maintenance_items: string[];
  service_requests: string[];
  honeydolist_items: string[];
}

interface MaintenanceItem {
  id: string;
  name: string;
  description: string;
  frequency: string;
  priority: 'high' | 'normal' | 'low';
  selected: boolean;
  next_due_date?: string;
}

interface ServiceRequestItem {
  id: string;
  title: string;
  description: string;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  status: string;
  selected: boolean;
}

interface HoneyDoItem {
  id: string;
  title: string;
  description: string;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  selected: boolean;
}

const VisitPlanning = () => {
  const { activeHome } = useActiveHome();
  const navigate = useNavigate();
  const location = useLocation();
  const visitIdFromState = (location.state as any)?.visitId;
  const createNewFromState = (location.state as any)?.createNew;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [visit, setVisit] = useState<Visit | null>(null);
  const [maintenanceItems, setMaintenanceItems] = useState<MaintenanceItem[]>([]);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequestItem[]>([]);
  const [honeyDoItems, setHoneyDoItems] = useState<HoneyDoItem[]>([]);

  useEffect(() => {
    console.log('VisitPlanning: visitIdFromState =', visitIdFromState, 'createNew =', createNewFromState);
    fetchData();
  }, [visitIdFromState, createNewFromState]);

  const fetchData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }

      // Get user's homes
      const homeIds = await getAccessibleHomeIds(user.id);

      if (homeIds.length === 0) {
        setLoading(false);
        return;
      }

      // If createNew flag is set, force creation of a new visit (skip existing visit check)
      if (createNewFromState) {
        console.log('Creating new visit (createNew flag set)');
        setVisit({
          id: 'new',
          scheduled_at: null,
          status: 'requested',
          home_id: activeHome?.id || homeIds[0],
          maintenance_items: [],
          service_requests: [],
          honeydolist_items: [],
        });

        await fetchAvailableItems(homeIds, [], [], []);
        setLoading(false);
        return;
      }

      // If visitId is provided, load that specific visit
      if (visitIdFromState) {
        console.log('Loading specific visit:', visitIdFromState);
        const { data: existingVisit, error: visitError } = await supabase
          .from('visits')
          .select('id, scheduled_date, status, home_id')
          .eq('id', visitIdFromState)
          .in('home_id', homeIds) // Ensure user owns this visit
          .single();

        console.log('Loaded visit:', existingVisit, 'Error:', visitError);

        if (visitError) {
          console.error('Error fetching specific visit:', visitError);
          // Fall through to create new visit
        } else if (existingVisit) {
          // Get existing visit tasks
          const { data: visitTasks } = await supabase
            .from('visit_tasks')
            .select('task_id, task_type')
            .eq('visit_id', existingVisit.id);

          console.log('Loaded visit tasks:', visitTasks);

          const existingMaintenanceIds = (visitTasks || [])
            .filter(t => t.task_type === 'maintenance_task')
            .map(t => t.task_id);
          
          const existingServiceRequestIds = (visitTasks || [])
            .filter(t => t.task_type === 'service_request')
            .map(t => t.task_id);
          
          const existingHoneyDoIds = (visitTasks || [])
            .filter(t => t.task_type === 'user_task')
            .map(t => t.task_id);

          console.log('Existing IDs - Maintenance:', existingMaintenanceIds, 'SR:', existingServiceRequestIds, 'HD:', existingHoneyDoIds);

          setVisit({
            id: existingVisit.id,
            scheduled_at: existingVisit.scheduled_date,
            status: existingVisit.status as 'scheduled' | 'completed' | 'cancelled' | 'requested',
            home_id: existingVisit.home_id,
            maintenance_items: existingMaintenanceIds,
            service_requests: existingServiceRequestIds,
            honeydolist_items: existingHoneyDoIds,
          });

          // Now fetch available items with proper selections
          await fetchAvailableItems(homeIds, existingMaintenanceIds, existingServiceRequestIds, existingHoneyDoIds);
          setLoading(false);
          return;
        }
      }

      // Check if there's an existing visit (only if no specific visitId provided)
      if (!visitIdFromState) {
        const { data: visits } = await supabase
          .from('visits')
          .select('id, scheduled_date, status, home_id')
          .in('home_id', homeIds)
          .eq('status', 'scheduled')
          .gte('scheduled_date', new Date().toISOString())
          .order('scheduled_date', { ascending: true })
          .limit(1);

        if (visits && visits.length > 0) {
          const existingVisit = visits[0];
          
          // Get existing visit tasks
          const { data: visitTasks } = await supabase
            .from('visit_tasks')
            .select('task_id, task_type')
            .eq('visit_id', existingVisit.id);

          const existingMaintenanceIds = (visitTasks || [])
            .filter(t => t.task_type === 'maintenance_task')
            .map(t => t.task_id);
          
          const existingServiceRequestIds = (visitTasks || [])
            .filter(t => t.task_type === 'service_request')
            .map(t => t.task_id);
          
          const existingHoneyDoIds = (visitTasks || [])
            .filter(t => t.task_type === 'user_task')
            .map(t => t.task_id);

          setVisit({
            id: existingVisit.id,
            scheduled_at: existingVisit.scheduled_date,
            status: existingVisit.status as 'scheduled' | 'completed' | 'cancelled' | 'requested',
            home_id: existingVisit.home_id,
            maintenance_items: existingMaintenanceIds,
            service_requests: existingServiceRequestIds,
            honeydolist_items: existingHoneyDoIds,
          });

          // Now fetch available items with proper selections
          await fetchAvailableItems(homeIds, existingMaintenanceIds, existingServiceRequestIds, existingHoneyDoIds);
          setLoading(false);
          return;
        }
      }

      // No existing visit, create placeholder
      setVisit({
        id: 'new',
        scheduled_at: null,
        status: 'requested',
        home_id: activeHome?.id || homeIds[0],
        maintenance_items: [],
        service_requests: [],
        honeydolist_items: [],
      });

      // Fetch available items with no selections
      await fetchAvailableItems(homeIds, [], [], []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableItems = async (
    homeIds: string[],
    existingMaintenanceIds: string[],
    existingServiceRequestIds: string[],
    existingHoneyDoIds: string[]
  ) => {
    try {
      const { data: maintenancePlans, error: maintenanceError } = await supabase
        .from('maintenance_plans')
        .select(`
          id,
          next_due_date,
          status,
          maintenance_tasks!inner (
            id,
            name,
            description,
            frequency_months
          )
        `)
        .in('home_id', homeIds)
        .in('status', ['pending', 'due', 'overdue']);

      if (maintenanceError) throw maintenanceError;

      const maintenanceData = (maintenancePlans || []).map(plan => {
        const task = Array.isArray(plan.maintenance_tasks) 
          ? plan.maintenance_tasks[0] 
          : plan.maintenance_tasks;
        
        const isSelected = existingMaintenanceIds.includes(plan.id);

        // Determine priority based on status or due date
        let priority: 'high' | 'normal' | 'low' = 'normal';
        if (plan.status === 'overdue') {
          priority = 'high';
        } else if (plan.status === 'due') {
          priority = 'normal';
        } else {
          priority = 'low';
        }

        return {
          id: plan.id,
          name: task.name,
          description: task.description || '',
          frequency: `Every ${task.frequency_months} month${task.frequency_months !== 1 ? 's' : ''}`,
          priority,
          next_due_date: plan.next_due_date,
          selected: isSelected,
        };
      });

      setMaintenanceItems(maintenanceData);

      // Fetch service requests
      const { data: serviceRequestsData, error: serviceRequestsError } = await supabase
        .from('service_requests')
        .select('id, title, description, priority, status')
        .in('home_id', homeIds)
        .in('status', ['pending', 'in_progress']);

      if (serviceRequestsError) throw serviceRequestsError;

      const serviceRequestData = (serviceRequestsData || []).map(sr => {
        const isSelected = existingServiceRequestIds.includes(sr.id);
        
        return {
          id: sr.id,
          title: sr.title,
          description: sr.description || '',
          priority: sr.priority as 'urgent' | 'high' | 'normal' | 'low',
          status: sr.status,
          selected: isSelected,
        };
      });

      setServiceRequests(serviceRequestData);

      // Fetch HoneyDo items
      const { data: honeyDoData, error: honeyDoError } = await supabase
        .from('honeydone_tasks')
        .select('id, title, description, priority')
        .in('home_id', homeIds)
        .in('status', ['pending', 'in_progress']);

      if (honeyDoError) throw honeyDoError;

      const honeyDoItemsData = (honeyDoData || []).map(task => {
        const isSelected = existingHoneyDoIds.includes(task.id);
        
        return {
          id: task.id,
          title: task.title,
          description: task.description || '',
          priority: (task.priority || 'normal') as 'urgent' | 'high' | 'normal' | 'low',
          selected: isSelected,
        };
      });

      setHoneyDoItems(honeyDoItemsData);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const formatVisitDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const toggleItem = (id: string, category: 'maintenance' | 'service' | 'honeydolist') => {
    if (isVisitTooSoonToEdit()) return;
    
    if (category === 'maintenance') {
      setMaintenanceItems(prev =>
        prev.map(item => (item.id === id ? { ...item, selected: !item.selected } : item))
      );
    } else if (category === 'service') {
      setServiceRequests(prev =>
        prev.map(item => (item.id === id ? { ...item, selected: !item.selected } : item))
      );
    } else {
      setHoneyDoItems(prev =>
        prev.map(item => (item.id === id ? { ...item, selected: !item.selected } : item))
      );
    }
  };

  const getAllItems = () => {
    const items = [
      ...maintenanceItems.map(item => ({
        id: item.id,
        title: item.name,
        category: 'Maintenance' as const,
        categoryColor: '#16a34a',
        selected: item.selected,
      })),
      ...serviceRequests.map(item => ({
        id: item.id,
        title: item.title,
        category: 'Service Request' as const,
        categoryColor: '#ea580c',
        selected: item.selected,
      })),
      ...honeyDoItems.map(item => ({
        id: item.id,
        title: item.title,
        category: 'HoneyDo List' as const,
        categoryColor: '#8b5cf6',
        selected: item.selected,
      })),
    ];
    
    return items;
  };

  const getSelectedCount = (items: Array<{ selected: boolean }>) => {
    return items.filter(item => item.selected).length;
  };

  const getTotalSelected = () => {
    return (
      getSelectedCount(maintenanceItems) +
      getSelectedCount(serviceRequests) +
      getSelectedCount(honeyDoItems)
    );
  };

  const isVisitTooSoonToEdit = () => {
    if (!visit || !visit.scheduled_at) return false;
    
    const visitDate = new Date(visit.scheduled_at);
    const now = new Date();
    const threeDaysFromNow = new Date(now.getTime() + (3 * 24 * 60 * 60 * 1000));
    
    // Return true if visit is in the past or within 3 days
    return visitDate <= threeDaysFromNow;
  };

  const handleSave = async () => {
    if (!visit) return;
    if (isVisitTooSoonToEdit()) return; // Prevent saving if too soon
    
    setSaving(true);
    try {
      // Get selected item IDs
      const selectedMaintenance = maintenanceItems.filter(i => i.selected).map(i => i.id);
      const selectedServiceRequests = serviceRequests.filter(i => i.selected).map(i => i.id);
      const selectedHoneyDo = honeyDoItems.filter(i => i.selected).map(i => i.id);

      // If this is a new visit, create it
      if (visit.id === 'new') {
        const { data: newVisit, error: visitError } = await supabase
          .from('visits')
          .insert({
            home_id: visit.home_id,
            visit_type: 'maintenance',
            status: 'requested',
            scheduled_date: visit.scheduled_at,
            homeowner_submitted_at: new Date().toISOString(),
            updated_by_homeowner: true,
            homeowner_updated_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (visitError) throw visitError;

        // Create visit tasks for selected items
        const visitTasks = [
          ...selectedMaintenance.map(id => ({
            visit_id: newVisit.id,
            task_id: id,
            task_type: 'maintenance_task',
            task_title: maintenanceItems.find(m => m.id === id)?.name || '',
            task_description: maintenanceItems.find(m => m.id === id)?.description || '',
            status: 'pending',
            requested_by_homeowner: true,
          })),
          ...selectedServiceRequests.map(id => ({
            visit_id: newVisit.id,
            task_id: id,
            task_type: 'service_request',
            task_title: serviceRequests.find(s => s.id === id)?.title || '',
            task_description: serviceRequests.find(s => s.id === id)?.description || '',
            status: 'pending',
            requested_by_homeowner: true,
          })),
          ...selectedHoneyDo.map(id => ({
            visit_id: newVisit.id,
            task_id: id,
            task_type: 'user_task',
            task_title: honeyDoItems.find(h => h.id === id)?.title || '',
            task_description: honeyDoItems.find(h => h.id === id)?.description || '',
            status: 'pending',
            requested_by_homeowner: true,
          })),
        ];

        if (visitTasks.length > 0) {
          const { error: tasksError } = await supabase
            .from('visit_tasks')
            .insert(visitTasks);

          if (tasksError) throw tasksError;
        }
      } else {
        // Delete existing visit tasks FIRST
        const { error: deleteError } = await supabase
          .from('visit_tasks')
          .delete()
          .eq('visit_id', visit.id);

        if (deleteError) throw deleteError;

        // Create new visit tasks for selected items
        const visitTasks = [
          ...selectedMaintenance.map(id => ({
            visit_id: visit.id,
            task_id: id,
            task_type: 'maintenance_task',
            task_title: maintenanceItems.find(m => m.id === id)?.name || '',
            task_description: maintenanceItems.find(m => m.id === id)?.description || '',
            status: 'pending',
            requested_by_homeowner: true,
          })),
          ...selectedServiceRequests.map(id => ({
            visit_id: visit.id,
            task_id: id,
            task_type: 'service_request',
            task_title: serviceRequests.find(s => s.id === id)?.title || '',
            task_description: serviceRequests.find(s => s.id === id)?.description || '',
            status: 'pending',
            requested_by_homeowner: true,
          })),
          ...selectedHoneyDo.map(id => ({
            visit_id: visit.id,
            task_id: id,
            task_type: 'user_task',
            task_title: honeyDoItems.find(h => h.id === id)?.title || '',
            task_description: honeyDoItems.find(h => h.id === id)?.description || '',
            status: 'pending',
            requested_by_homeowner: true,
          })),
        ];

        if (visitTasks.length > 0) {
          const { error: tasksError } = await supabase
            .from('visit_tasks')
            .insert(visitTasks);

          if (tasksError) throw tasksError;
        }

        // Update visit LAST - this triggers notification with correct task list
        const { error: updateError } = await supabase
          .from('visits')
          .update({
            homeowner_submitted_at: new Date().toISOString(),
            updated_by_homeowner: true,
            homeowner_updated_at: new Date().toISOString(),
          })
          .eq('id', visit.id);

        if (updateError) throw updateError;
      }

      // Navigate to confirmation page
      navigate('/visit-confirmation');
    } catch (error) {
      console.error('Error saving visit plan:', error);
    } finally {
      setSaving(false);
    }
  };

  const getCategoryType = (category: 'Maintenance' | 'Service Request' | 'HoneyDo List'): 'maintenance' | 'service' | 'honeydolist' => {
    if (category === 'Maintenance') return 'maintenance';
    if (category === 'Service Request') return 'service';
    return 'honeydolist';
  };

  if (loading) {
    return (
      <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <View style={styles.header}>
          <Pressable
            onPress={() => navigate('/dashboard')}
            style={styles.backButton}
          >
            <ArrowLeft size={20} color="#6b7280" />
            <Text style={styles.backButtonText}>Back</Text>
          </Pressable>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#9ca3af" />
          <Text style={styles.loadingText}>Loading visit details...</Text>
        </View>
      </WebScrollView>
    );
  }

  if (!visit) {
    return (
      <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <View style={styles.header}>
          <Pressable
            onPress={() => navigate('/dashboard')}
            style={styles.backButton}
          >
            <ArrowLeft size={20} color="#6b7280" />
            <Text style={styles.backButtonText}>Back</Text>
          </Pressable>
        </View>
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>Unable to load visit data</Text>
        </View>
      </WebScrollView>
    );
  }

  const tooSoonToEdit = isVisitTooSoonToEdit();

  return (
    <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Header */}
      <View style={styles.header}>
        <Pressable
          onPress={() => navigate('/dashboard')}
          style={styles.backButton}
        >
          <ArrowLeft size={20} color="#6b7280" />
          <Text style={styles.backButtonText}>Back</Text>
        </Pressable>
        {!tooSoonToEdit && (
          <Pressable
            onPress={handleSave}
            disabled={saving}
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <>
                <Check size={16} color="#ffffff" />
                <Text style={styles.saveButtonText}>
                  {visit.scheduled_at ? 'Save' : 'Submit Request'}
                </Text>
              </>
            )}
          </Pressable>
        )}
      </View>

      {/* Too Soon to Edit Warning */}
      {tooSoonToEdit && (
        <View style={styles.warningCard}>
          <View style={styles.warningContent}>
            <Calendar size={24} color="#ea580c" />
            <View style={styles.warningText}>
              <Text style={styles.warningTitle}>Changes Not Allowed</Text>
              <Text style={styles.warningDescription}>
                This visit is scheduled within 3 days or has passed. Changes cannot be made at this time. Please contact us if you need to make adjustments.
              </Text>
            </View>
          </View>
        </View>
      )}

      {/* Visit Info */}
      <View style={styles.visitInfoCard}>
        <View style={styles.visitInfoHeader}>
          <Calendar size={24} color="#111827" />
          <Text style={styles.visitInfoTitle}>HoneyDone Visit</Text>
        </View>
        {visit.scheduled_at ? (
          <>
            <Text style={styles.visitInfoDate}>{formatVisitDate(visit.scheduled_at)}</Text>
            <View style={styles.visitInfoSummary}>
              <Text style={styles.visitInfoSummaryText}>
                {getTotalSelected()} {getTotalSelected() === 1 ? 'item' : 'items'} selected for this visit
              </Text>
            </View>
          </>
        ) : (
          <>
            <View style={styles.visitRequestInfo}>
              <Text style={styles.visitRequestDescription}>
                Select the items you'd like us to handle. We'll review your request and follow up to confirm details and schedule your visit.
              </Text>
            </View>
            <View style={styles.visitInfoSummary}>
              <Text style={styles.visitInfoSummaryText}>
                {getTotalSelected()} {getTotalSelected() === 1 ? 'item' : 'items'} selected
              </Text>
            </View>
          </>
        )}
      </View>

      {/* All Items Section */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Select Items for Visit</Text>
          <View style={styles.countBadge}>
            <Text style={styles.countBadgeText}>
              {getTotalSelected()}/{getAllItems().length}
            </Text>
          </View>
        </View>

        <View style={styles.itemsList}>
          {getAllItems().map(item => (
            <Pressable
              key={`${item.category}-${item.id}`}
              onPress={() => toggleItem(item.id, getCategoryType(item.category))}
              disabled={tooSoonToEdit}
              style={({ pressed }) => [
                styles.itemCard,
                item.selected && styles.itemCardSelected,
                pressed && !tooSoonToEdit && styles.itemCardPressed,
                tooSoonToEdit && styles.itemCardDisabled,
              ]}
            >
              <View style={styles.itemCardLeft}>
                <View
                  style={[
                    styles.checkbox,
                    item.selected && styles.checkboxSelected,
                  ]}
                >
                  {item.selected && <Check size={16} color="#ffffff" />}
                </View>
                <View style={styles.itemContent}>
                  <Text style={styles.itemTitle}>{item.title}</Text>
                  <View style={[styles.categoryBadge, { backgroundColor: `${item.categoryColor}15` }]}>
                    <Text style={[styles.categoryBadgeText, { color: item.categoryColor }]}>
                      {item.category}
                    </Text>
                  </View>
                </View>
              </View>
            </Pressable>
          ))}
        </View>
      </View>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  contentContainer: {
    padding: 16,
    gap: 32,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      paddingBottom: 32,
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingRight: 16,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  backButtonText: {
    fontSize: 16,
    color: '#6b7280',
    fontWeight: '500',
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  visitInfoCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    gap: 12,
  },
  visitInfoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  visitInfoTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  visitInfoDate: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
  },
  visitRequestInfo: {
    gap: 8,
  },
  visitRequestTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  visitRequestDescription: {
    fontSize: 15,
    color: '#4b5563',
    lineHeight: 22,
  },
  visitInfoSummary: {
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  visitInfoSummaryText: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },
  section: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    backgroundColor: '#ffffff',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  countBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: '#f3f4f6',
    borderRadius: 12,
  },
  countBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
  },
  itemsList: {
    gap: 0,
  },
  itemCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
  },
  itemCardSelected: {
    backgroundColor: '#f9fafb',
  },
  itemCardPressed: {
    backgroundColor: '#f3f4f6',
  },
  itemCardLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#d1d5db',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    marginTop: 2,
  },
  checkboxSelected: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  itemContent: {
    flex: 1,
    gap: 8,
  },
  itemTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  categoryBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    color: '#6b7280',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
  },
  emptyText: {
    fontSize: 16,
    color: '#6b7280',
  },
  warningCard: {
    backgroundColor: '#fff7ed',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#fed7aa',
  },
  warningContent: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  warningText: {
    flex: 1,
    gap: 8,
  },
  warningTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#c2410c',
  },
  warningDescription: {
    fontSize: 14,
    color: '#9a3412',
    lineHeight: 20,
  },
  itemCardDisabled: {
    opacity: 0.6,
  },
});

export default VisitPlanning;

